import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  Stack,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
} from '@mui/material';
import { X, MessageCircle, Smartphone, Mail, CheckCircle2, XCircle } from 'lucide-react';
import { api } from '../../lib/api';
import { safeConsole } from '../../lib/piiProtection';

interface PrescriptionShareDialogProps {
  open: boolean;
  onClose: () => void;
  prescriptionId: string;
}

type Channel = 'WHATSAPP' | 'SMS' | 'EMAIL';
type Outcome = { channel: string; success: boolean; error: string | null };

/**
 * Share an issued prescription with the patient — WhatsApp/SMS carry a secure, short-lived
 * tokenized link (no PHI in the message body); Email attaches the PDF directly and includes the
 * same link as a fallback. Repeatable — a doctor can resend if the patient says they didn't
 * receive it (unlike the once-only scheduled reminder channels used elsewhere in the app).
 */
export function PrescriptionShareDialog({
  open,
  onClose,
  prescriptionId,
}: PrescriptionShareDialogProps) {
  const [channels, setChannels] = useState<Record<Channel, boolean>>({
    WHATSAPP: true,
    SMS: false,
    EMAIL: true,
  });
  const [sending, setSending] = useState(false);
  const [outcomes, setOutcomes] = useState<Outcome[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const toggle = (c: Channel) => setChannels((prev) => ({ ...prev, [c]: !prev[c] }));

  const selectedChannels = (Object.keys(channels) as Channel[]).filter((c) => channels[c]);

  const handleSend = async () => {
    if (selectedChannels.length === 0) {
      setError('Select at least one channel');
      return;
    }
    setSending(true);
    setError(null);
    setOutcomes(null);
    try {
      const res = await api.prescriptions.share(prescriptionId, selectedChannels);
      if (res.error) {
        setError(res.error);
        return;
      }
      setOutcomes(Object.values(res.data ?? {}));
    } catch (err) {
      safeConsole.error('Prescription share failed:', err);
      setError('Could not share prescription. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setOutcomes(null);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" fontWeight={700}>
          Share with Patient
        </Typography>
        <IconButton size="small" onClick={handleClose}>
          <X size={18} />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Sends a secure link the patient can open without logging in. Email also attaches the prescription PDF.
        </Typography>

        <Stack spacing={1}>
          <FormControlLabel
            control={<Checkbox checked={channels.WHATSAPP} onChange={() => toggle('WHATSAPP')} />}
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <MessageCircle size={16} />
                <span>WhatsApp</span>
              </Stack>
            }
          />
          <FormControlLabel
            control={<Checkbox checked={channels.SMS} onChange={() => toggle('SMS')} />}
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <Smartphone size={16} />
                <span>SMS</span>
              </Stack>
            }
          />
          <FormControlLabel
            control={<Checkbox checked={channels.EMAIL} onChange={() => toggle('EMAIL')} />}
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <Mail size={16} />
                <span>Email (PDF attached)</span>
              </Stack>
            }
          />
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          If the patient has no mobile number or email on file, that channel will report a failure below.
        </Typography>

        {error && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {outcomes && (
          <Stack spacing={0.5} sx={{ mt: 2 }}>
            {outcomes.map((o) => (
              <Box key={o.channel} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {o.success ? (
                  <CheckCircle2 size={16} color="green" />
                ) : (
                  <XCircle size={16} color="#d32f2f" />
                )}
                <Typography variant="body2">
                  {o.channel}: {o.success ? 'Sent' : o.error || 'Failed'}
                </Typography>
                {o.success && <Chip label="sent" size="small" color="success" variant="outlined" sx={{ height: 18, fontSize: 10 }} />}
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose}>Close</Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={sending || selectedChannels.length === 0}
          startIcon={sending ? <CircularProgress size={14} /> : undefined}
        >
          {sending ? 'Sending...' : 'Send'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
